import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ProgressiveOnboardingDemoAgentic.module.css';

type Choice = 'pending' | 'creating' | 'created' | 'snoozed' | 'optedOut';

interface LogEntry {
  id: number;
  time: string;
  text: string;
  state: 'done' | 'active' | 'idle';
  tag?: string;
}

const TIMES = ['10:30 PM', '11 PM', '11:30 PM'];

const TIME_24: Record<string, string> = { '10:30 PM': '22:30', '11 PM': '23:00', '11:30 PM': '23:30' };

const DOT: Record<LogEntry['state'], string> = {
  done: kit.logDotDone,
  active: kit.logDotActive,
  idle: '',
};

const MEMORY: Record<Choice, string> = {
  pending: 'Routines: suggesting now',
  creating: 'Routines: introduced, accepted',
  created: 'Routines: introduced, accepted - won\'t pitch again',
  snoozed: 'Routines: snoozed until next week',
  optedOut: 'Routines: opted out - won\'t suggest again',
};

export function ProgressiveOnboardingDemoAgentic() {
  const [choice, setChoice] = useState<Choice>('pending');
  const [time, setTime] = useState('11 PM');
  const [paused, setPaused] = useState(false);
  const [nights, setNights] = useState(0);
  const [log, setLog] = useState<LogEntry[]>([
    { id: 1, time: 'Mon-Thu', text: 'You turned off downstairs lights by hand around 11 PM', state: 'idle' },
    { id: 2, time: '23:04', text: 'You turned off 6 downstairs lights', state: 'done' },
  ]);
  const [feedback, setFeedback] = useState(
    'The agent ties a capability to something you just did, with the rule pre-filled. Adjust it or choose.'
  );
  const idRef = useRef(2);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const list = timers.current;
    return () => list.forEach(clearTimeout);
  }, []);

  const push = (entry: Omit<LogEntry, 'id'>) => {
    idRef.current += 1;
    const id = idRef.current;
    setLog((l) => [...l, { ...entry, id }]);
    return id;
  };

  const setUp = () => {
    setChoice('creating');
    const id = push({ time: '23:05', text: 'Creating routine', state: 'active' });
    setFeedback('Setting it up exactly as shown - nothing beyond the rule you saw.');
    timers.current.push(
      setTimeout(() => {
        setLog((l) =>
          l.map((e) =>
            e.id === id
              ? { ...e, text: `Routine created: Downstairs lights off, nightly ${time}`, state: 'done' }
              : e
          )
        );
        setChoice('created');
        setFeedback('Done. It points you to where the routine lives, so you can change it later.');
      }, 1000)
    );
  };

  const snooze = () => {
    setChoice('snoozed');
    push({ time: '23:05', text: 'Suggestion snoozed for a week', state: 'done' });
    setFeedback('Deferred, not dropped. Simulate next week to see it come back once.');
  };

  const optOut = () => {
    setChoice('optedOut');
    push({ time: '23:05', text: 'Routine suggestions turned off. Routines still available in Home > Routines', state: 'done' });
    setFeedback('No more pitches. The feature itself is still there whenever you look for it.');
  };

  const simulate = () => {
    const n = nights + 1;
    setNights(n);
    if (choice === 'created') {
      push({
        time: paused ? '23:06' : TIME_24[time],
        text: paused ? 'You turned off 6 downstairs lights' : 'Routine ran: downstairs lights off',
        state: 'done',
        tag: paused ? 'Paused' : undefined,
      });
      setFeedback(
        paused
          ? 'Routine is paused, so you did it by hand. The agent does not re-pitch a feature you already know.'
          : 'The routine ran on its own. No new suggestions - it knows you have seen this one.'
      );
      return;
    }
    if (choice === 'snoozed') {
      push({ time: 'Next Mon', text: 'You turned off 6 downstairs lights', state: 'done' });
      setChoice('pending');
      setFeedback('A week later the habit is still there, so the suggestion comes back - once, as promised.');
      return;
    }
    push({ time: `Night ${n}`, text: 'You turned off 6 downstairs lights', state: 'done' });
    setFeedback('No suggestion. You opted out, and the agent respects it.');
  };

  const showSuggestion = choice === 'pending';

  return (
    <div className={kit.root}>
      <div className={kit.log} aria-label="Home activity">
        {log.map((e) => (
          <div key={e.id} className={kit.logRow}>
            <span className={kit.logTime}>{e.time}</span>
            <span className={`${kit.logDot} ${DOT[e.state]}`} aria-hidden="true" />
            <span>{e.text}</span>
            {e.tag && <span className={kit.chip}>{e.tag}</span>}
          </div>
        ))}
      </div>

      {showSuggestion && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Suggested routine</span>
            <span>{nights > 0 ? 'Reminder you asked for' : 'Based on this week'}</span>
          </div>
          <h3 className={kit.proposalTitle}>
            You've done this around 11 PM five nights this week. I can do it as a routine.
          </h3>
          <p className={styles.rule}>
            "Every night at {time}, turn off downstairs lights."
          </p>
          <div className={`${kit.row} ${styles.times}`} role="group" aria-label="Routine time">
            {TIMES.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={t === time}
                className={`${kit.chip} ${styles.timeChip} ${t === time ? styles.timeChipOn : ''}`}
                onClick={() => {
                  setTime(t);
                  setFeedback(`Rule updated to ${t}. The example is the actual rule you'd get.`);
                }}
              >
                {t}
              </button>
            ))}
          </div>
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={setUp}>
              Set up routine
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={snooze}>
              Remind me later
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={optOut}>
              Don't suggest this
            </button>
          </div>
        </div>
      )}

      {choice === 'created' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Home &gt; Routines</span>
            <span>{paused ? 'Paused' : 'Active'}</span>
          </div>
          <h3 className={kit.proposalTitle}>Downstairs lights off</h3>
          <dl className={kit.fields}>
            <dt>When</dt>
            <dd>Every night at {time}</dd>
            <dt>Does</dt>
            <dd>Turns off 6 downstairs lights</dd>
          </dl>
          <div className={`${kit.row} ${styles.manage}`}>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnEdit}`}
              onClick={() => {
                setPaused((p) => !p);
                setFeedback(paused ? 'Routine resumed.' : 'Routine paused. It stays in Routines until you resume it.');
              }}
            >
              {paused ? 'Resume' : 'Pause'}
            </button>
          </div>
        </div>
      )}

      {choice !== 'pending' && choice !== 'creating' && (
        <div className={kit.row}>
          <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={simulate}>
            {choice === 'snoozed' ? 'Simulate next week' : 'Simulate next night'}
          </button>
        </div>
      )}

      <div className={kit.row}>
        <span className={kit.label}>AGENT MEMORY</span>
        <span className={kit.chip}>{MEMORY[choice]}</span>
      </div>

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
